import React from "react";
import Link from "@/components/Link";
import { TIER_HOVER_AR } from "@shared/gamification";

// The author's contribution TITLE, rendered wherever a reader sees a native review or a profile.
//
// Two rules this component exists to make unbreakable (spec §1, §9):
//
//  - it takes a `tier`, never a points figure, so no caller can accidentally render the raw
//    number on a review card;
//  - it is styled as a quiet neutral pill, NOT as a trust badge. Tier measures contribution
//    volume; verification («موثّق», «عميل موثق») is the trust axis and the only one allowed to
//    look authoritative. A tier-5 account can still be a farm, and the chip must not suggest
//    otherwise by borrowing the verification badge's green.
//
// The hover copy is a `title` attribute rather than a shadcn Tooltip on purpose: the chip renders
// inside `ReviewCard` on the SSR'd /store/@id route, where no TooltipProvider wraps the tree.

export type TierChipProps = {
  tier: { level: number; ar: string };
  href?: string;
  onClick?: () => void;
  // Slightly larger on a profile's identity card than in a review header.
  size?: "sm" | "md";
};

const TierChip: React.FC<TierChipProps> = ({ tier, href, onClick, size = "sm" }) => {
  if (!tier?.ar) return null;

  const style: React.CSSProperties = {
    display: "inline-block",
    fontFamily: "var(--ys-font)",
    fontSize: size === "md" ? 12 : 11,
    fontWeight: 500,
    color: "var(--ys-ink-2)",
    background: "var(--ys-bg-2)",
    border: "1px solid var(--ys-rule)",
    borderRadius: "var(--ys-r-pill)",
    padding: size === "md" ? "2px 9px" : "1px 7px",
    textDecoration: "none",
    whiteSpace: "nowrap",
  };

  const label = (
    <span style={style} title={TIER_HOVER_AR[tier.level]}>
      {tier.ar}
    </span>
  );

  if (!href) return label;

  return (
    <Link to={href} onClick={onClick} style={{ textDecoration: "none" }}>
      {label}
    </Link>
  );
};

export default TierChip;
